import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Download,
  Calendar,
  Package,
  Truck,
  Ship
} from 'lucide-react';

const Livraisons = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tous');

  const livraisons = [
    {
      id: '21101',
      dateLivraison: '2024-01-15',
      fournisseur: 'Maersk Line',
      bonLivraison: 'BL-001-2024',
      destinataire: 'CMA CGM',
      numeroDossier: 'DOS-2024-01-001',
      conditionnement: 'Conteneur 40\'',
      poids: '2.5 tonnes',
      dimensions: '12.2 x 2.4 x 2.6 m',
      tracking: 'UPS-123456789',
      douane: 'Oui - DCL-2024-001',
      numeroCommande: 'CMD-CMA-001',
      status: 'En cours'
    },
    {
      id: '21102',
      dateLivraison: '2024-01-14',
      fournisseur: 'Hapag-Lloyd',
      bonLivraison: 'BL-002-2024',
      destinataire: 'MSC',
      numeroDossier: 'DOS-2024-01-002',
      conditionnement: 'Palette',
      poids: '1.8 tonnes',
      dimensions: '1.2 x 0.8 x 1.5 m',
      tracking: 'DHL-987654321',
      douane: 'Non',
      numeroCommande: 'CMD-MSC-001',
      status: 'Terminée'
    },
    {
      id: '21103',
      dateLivraison: '2024-01-13',
      fournisseur: 'ONE',
      bonLivraison: 'BL-003-2024',
      destinataire: 'Evergreen',
      numeroDossier: 'DOS-2024-01-003',
      conditionnement: 'Conteneur 20\'',
      poids: '3.2 tonnes',
      dimensions: '6.1 x 2.4 x 2.6 m',
      tracking: 'Pickup',
      douane: 'Oui - DCL-2024-002',
      numeroCommande: 'CMD-EVG-001',
      status: 'Terminée'
    },
    {
      id: '21104',
      dateLivraison: '2024-01-12',
      fournisseur: 'MSC',
      bonLivraison: 'BL-004-2024',
      destinataire: 'COSCO',
      numeroDossier: 'DOS-2024-01-004',
      conditionnement: 'Conteneur 40\'',
      poids: '4.1 tonnes',
      dimensions: '12.2 x 2.4 x 2.6 m',
      tracking: 'FedEx-456789123',
      douane: 'Non',
      numeroCommande: 'CMD-COS-001',
      status: 'Terminée'
    }
  ];

  const filteredLivraisons = livraisons.filter(livraison => {
    const matchesSearch = 
      livraison.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      livraison.destinataire.toLowerCase().includes(searchTerm.toLowerCase()) ||
      livraison.fournisseur.toLowerCase().includes(searchTerm.toLowerCase()) ||
      livraison.numeroCommande.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'Tous' || livraison.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Terminée':
        return 'bg-atl-green-100 text-atl-green-800';
      case 'En cours':
        return 'bg-atl-blue-100 text-atl-blue-800';
      case 'En attente':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-port-gray-100 text-port-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-port-gray-900">Gestion des livraisons</h1>
          <p className="text-port-gray-600">Suivi et gestion des livraisons ATL</p>
        </div>
        <button className="btn-primary flex items-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>Nouvelle livraison</span>
        </button>
      </div>

      {/* Filtres et recherche */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par ID, client, fournisseur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="Tous">Tous les statuts</option>
              <option value="En cours">En cours</option>
              <option value="Terminée">Terminée</option>
              <option value="En attente">En attente</option>
            </select>
            <button className="btn-secondary flex items-center space-x-2">
              <Filter className="h-5 w-5" />
              <span>Filtres</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tableau des livraisons */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-port-gray-200">
            <thead className="bg-port-gray-50">
              <tr>
                <th className="table-header px-6 py-3 text-left">ID Livraison</th>
                <th className="table-header px-6 py-3 text-left">Date</th>
                <th className="table-header px-6 py-3 text-left">Client</th>
                <th className="table-header px-6 py-3 text-left">Fournisseur</th>
                <th className="table-header px-6 py-3 text-left">Conditionnement</th>
                <th className="table-header px-6 py-3 text-left">Poids</th>
                <th className="table-header px-6 py-3 text-left">Douane</th>
                <th className="table-header px-6 py-3 text-left">Statut</th>
                <th className="table-header px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-port-gray-200">
              {filteredLivraisons.map((livraison) => (
                <tr key={livraison.id} className="table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Package className="h-5 w-5 text-atl-blue-600 mr-2" />
                      <span className="font-medium text-port-gray-900">{livraison.id}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-port-gray-400 mr-2" />
                      <span className="text-port-gray-900">{livraison.dateLivraison}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-port-gray-900">{livraison.destinataire}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-port-gray-900">{livraison.fournisseur}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-port-gray-900">{livraison.conditionnement}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-port-gray-900">{livraison.poids}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      livraison.douane.startsWith('Oui') ? 'bg-red-100 text-red-800' : 'bg-atl-green-100 text-atl-green-800'
                    }`}>
                      {livraison.douane.startsWith('Oui') ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(livraison.status)}`}>
                      {livraison.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button className="text-atl-blue-600 hover:text-atl-blue-700">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="text-port-gray-600 hover:text-port-gray-700">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-atl-green-600 hover:text-atl-green-700">
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-port-gray-700">
          Affichage de <span className="font-medium">1</span> à <span className="font-medium">{filteredLivraisons.length}</span> sur <span className="font-medium">{livraisons.length}</span> résultats
        </div>
        <div className="flex items-center space-x-2">
          <button className="btn-secondary">Précédent</button>
          <button className="btn-primary">Suivant</button>
        </div>
      </div>
    </div>
  );
};

export default Livraisons; 